import React, {useEffect, useRef, useState, useCallback} from 'react';
import root from 'window-or-global';
import {useBreakpointDown, AccentColorOptions} from 'epic-ue-theme';
import {useScroll} from '../../../shared';
import {
    MaskedHeaderScrollIndicatorWrapper,
    MaskedHeaderWrapper,
    BottomContent
} from './MaskedHeader.styles';
import {
    VerticalSpacer,
    Button,
    CmsButtonProps,
    AboveFoldScrollIndicator,
    GridContainer,
    SectionInnerGridContent,
    Typography
} from '../../elements';
import {motion, useTransform, useMotionValue, useMotionTemplate} from 'framer-motion';
import classNames from 'classnames';
import {MaskedHeaderMobile} from './MaskedHeader.mobile';
import {MaskedHeaderIcon} from './MaskedHeader.icon';
import {MaskedHeaderGradient} from './MaskedHeader.gradient';

export interface MaskedHeaderProps {
    svg: string;
    video: string;
    videoPlaceholder?: string;
    title?: string;
    title2?: string;
    description?: string;
    button?: CmsButtonProps;
    accentColor?: AccentColorOptions;
}

export const MaskedHeaderNormal = ({
    svg = '',
    video,
    videoPlaceholder,
    title = '',
    title2 = '',
    description = '',
    button,
    accentColor
}: MaskedHeaderProps): JSX.Element => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [videoEnded, setVideoEnded] = useState(false);
    const [wheel, setWheel] = useState(false);
    const [vh, setVh] = useState(root.innerHeight || 1000);
    const [duration, setDuration] = useState<number>(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    const initDuration = useCallback(() => {
        if (videoRef?.current?.duration && duration !== videoRef.current.duration) {
            setDuration(videoRef?.current?.duration || 0);
        }
    }, [duration, setDuration]);

    const setHeight = useCallback(() => {
        const newVh = root.innerHeight || 0;
        if (newVh !== vh) {
            setVh(newVh);
        }
    }, [vh, setVh]);

    useEffect(() => {
        setHeight();
        initDuration();

        root.addEventListener('resize', setHeight);
        return () => {
            root.removeEventListener('resize', setHeight);
        };
    }, [setHeight]);

    const {position} = useScroll();
    const scrollY = useMotionValue(0);
    const y = position.y;
    scrollY.set(y);

    const offsetHeight = vh / 1.6;

    let halfway = false;
    if (vh && y >= vh) {
        halfway = true;
    }

    const videoCompletePosition = vh * 2;
    const finalPosition = vh * 3;

    let isComplete = false;
    if (vh && y >= finalPosition) {
        isComplete = true;
    }

    let videoComplete = false;
    if (vh && y >= videoCompletePosition) {
        videoComplete = true;
    }

    const percentScrolled = y / videoCompletePosition;
    const currentTime = +(duration * percentScrolled).toFixed(4);

    //translate out the title
    const textTranslate = useTransform(scrollY, [vh - 300, vh], [0, 80]);
    const textOpacity = useTransform(scrollY, [vh - 300, vh], [1, 0]);

    //translate in title2
    const title2TranslateIn = useTransform(scrollY, [vh, vh + 300], [-80, 0]);
    const title2OpacityIn = useTransform(scrollY, [vh, vh + 300], [0, 1]);

    //translate out title2
    const title2TranslateOut = useTransform(
        scrollY,
        [videoCompletePosition, videoCompletePosition + 400],
        [0, -80]
    );
    const title2OpacityOut = useTransform(
        scrollY,
        [videoCompletePosition, videoCompletePosition + 400],
        [1, 0]
    );

    //scale and fade out the video down after completed
    const scaleVideo = useTransform(scrollY, [videoCompletePosition, finalPosition], [1, 0.5]);
    const opacityVideo = useTransform(
        scrollY,
        [videoCompletePosition + 400, finalPosition],
        [1, 0.5]
    );

    //apply a blur to the filter as it is scaling down
    const filterVideo = useTransform(scrollY, [videoCompletePosition, finalPosition], [0, 30]);
    const blur = useMotionTemplate`blur(${filterVideo}px)`;

    //change the perspective of the video
    const tiltVideo = useTransform(scrollY, [videoCompletePosition, finalPosition], [0, -40]);

    useEffect(() => {
        if (!hasLoaded) {
            setHasLoaded(true);
        }
    }, [setHasLoaded, hasLoaded]);

    const ended = useCallback(() => {
        setVideoEnded(true);
    }, [setVideoEnded]);

    //reset everything when scrolled back to top
    useEffect(() => {
        if (y === 0) {
            setVideoEnded(false);
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [y, videoRef, setVideoEnded, setWheel]);

    useEffect(() => {
        if (currentTime && videoRef.current && videoRef.current.currentTime !== currentTime) {
            if (!videoEnded) {
                videoRef.current.play();
            }
        }
    }, [wheel, videoRef, currentTime, videoEnded]);

    return (
        <>
            <MaskedHeaderWrapper className={classNames({hasLoaded, isComplete})}>
                <MaskedHeaderGradient />
                <MaskedHeaderIcon svg={svg} />

                <motion.div
                    className="masked-video will-change-transform"
                    key="masked-video"
                    style={{
                        scale: scaleVideo,
                        filter: blur,
                        opacity: opacityVideo
                    }}>
                    <div className="perspective">
                        <motion.div
                            className="will-change-transform"
                            style={{
                                rotateX: tiltVideo
                            }}>
                            <video
                                id="maskedVideo"
                                ref={videoRef}
                                onLoadedMetadata={initDuration}
                                onCanPlay={initDuration}
                                muted
                                autoPlay={false}
                                preload="auto"
                                onEnded={ended}
                                controls={false}
                                disablePictureInPicture
                                playsInline
                                poster={videoPlaceholder}>
                                <source src={video} type="video/mp4" />
                            </video>
                        </motion.div>
                    </div>
                </motion.div>

                <div className={classNames('above-fold-heading', {halfway, isComplete})}>
                    <motion.div
                        className="title-1 will-change-transform-opacity"
                        style={{
                            translateY: textTranslate,
                            opacity: textOpacity
                        }}>
                        <Typography variant="h1">{title || ''}</Typography>
                    </motion.div>
                    <motion.div
                        className="title-2 will-change-transform-opacity"
                        style={{
                            translateY: videoComplete ? title2TranslateOut : title2TranslateIn,
                            opacity: videoComplete ? title2OpacityOut : title2OpacityIn
                        }}>
                        <Typography variant="h1">{title2 || ''}</Typography>
                    </motion.div>
                </div>
            </MaskedHeaderWrapper>

            <MaskedHeaderScrollIndicatorWrapper className={classNames({hide: videoComplete})}>
                <AboveFoldScrollIndicator />
            </MaskedHeaderScrollIndicatorWrapper>

            <BottomContent $offsetHeight={offsetHeight}>
                {description ? (
                    <GridContainer key="ue-masked-header-desc">
                        <SectionInnerGridContent>
                            <VerticalSpacer variant="heading" />
                            <Typography variant="h2">{description}</Typography>
                            <VerticalSpacer variant="heading" />
                            <Button
                                type={button?.type || 'primary'}
                                href={button?.href}
                                color={accentColor}>
                                {button?.label}
                            </Button>
                        </SectionInnerGridContent>
                    </GridContainer>
                ) : (
                    ''
                )}
                <VerticalSpacer variant="section" />
            </BottomContent>

            <VerticalSpacer variant="section" />
        </>
    );
};

export const MaskedHeader = (props: MaskedHeaderProps): JSX.Element => {
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!hasLoaded) {
            if ('scrollRestoration' in root.history) {
                root.history.scrollRestoration = 'manual';
            }
            root.scrollTo(0, 0);
            setHasLoaded(true);
        }
    }, [setHasLoaded, hasLoaded]);

    const downSm = useBreakpointDown('sm');

    if (downSm) {
        return <MaskedHeaderMobile {...props} />;
    }

    return <MaskedHeaderNormal {...props} />;
};
